import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  MessageSquare,
  Filter,
  RefreshCw,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-gold/20 text-gold border-gold/40" },
  {
    value: "contacted",
    label: "Contacted",
    color: "bg-blue-400/15 text-blue-200 border-blue-400/30",
  },
  {
    value: "quoted",
    label: "Quoted",
    color: "bg-purple-400/15 text-purple-200 border-purple-400/30",
  },
  {
    value: "confirmed",
    label: "Confirmed",
    color: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
  },
  {
    value: "declined",
    label: "Declined",
    color: "bg-white/5 text-white/40 border-white/10",
  },
];

const statusMeta = (s) =>
  STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { admin, logout, authedRequest } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, confirmed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        authedRequest({ url: "/admin/bookings", method: "GET" }),
        authedRequest({ url: "/admin/stats", method: "GET" }),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      toast.error("Could not load bookings", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    } finally {
      setLoading(false);
    }
  }, [authedRequest]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { data } = await authedRequest({
        url: `/admin/bookings/${id}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: { status: newStatus },
      });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selected?.id === id) setSelected(data);
      toast.success("Status updated", {
        description: `Marked as ${newStatus}`,
      });
      // refresh stats
      const s = await authedRequest({ url: "/admin/stats", method: "GET" });
      setStats(s.data);
    } catch (err) {
      toast.error("Update failed", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      const { data } = await authedRequest({
        url: `/admin/bookings/${id}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: { notes },
      });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      setSelected(data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Save failed", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking enquiry permanently?")) return;
    try {
      await authedRequest({ url: `/admin/bookings/${id}`, method: "DELETE" });
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setSelected(null);
      toast.success("Booking deleted");
      const s = await authedRequest({ url: "/admin/stats", method: "GET" });
      setStats(s.data);
    } catch (err) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(err?.response?.data?.detail),
      });
    }
  };

  const filtered =
    filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      {/* Header */}
      <header
        data-testid="admin-header"
        className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-30"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-display text-2xl tracking-tight text-ivory">
              Givance
            </span>
            <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-gold" />
            <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-white/40">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-white/50">
              {admin?.email}
            </span>
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="p-2 hover:bg-white/5 transition-colors"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 text-white/60 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-ivory transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Page heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
              Bookings
            </p>
            <h1
              className="font-display text-4xl sm:text-5xl text-ivory"
              data-testid="admin-heading"
            >
              Enquiries
            </h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          {[
            { label: "Total", value: stats.total, k: "total" },
            { label: "New", value: stats.new, k: "new" },
            { label: "Contacted", value: stats.contacted, k: "contacted" },
            { label: "Confirmed", value: stats.confirmed, k: "confirmed" },
          ].map((s) => (
            <div
              key={s.label}
              data-testid={`stat-${s.k}`}
              className="border border-white/10 p-6 bg-[#18181b]/50"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                {s.label}
              </div>
              <div className="mt-2 font-display text-4xl text-ivory">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <Filter className="w-4 h-4 text-white/40" />
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`text-xs px-3 py-1.5 border transition-all ${
              filter === "all"
                ? "border-gold text-gold"
                : "border-white/15 text-white/50 hover:border-white/40"
            }`}
          >
            All ({bookings.length})
          </button>
          {STATUS_OPTIONS.map((o) => {
            const count = bookings.filter((b) => b.status === o.value).length;
            return (
              <button
                key={o.value}
                data-testid={`filter-${o.value}`}
                onClick={() => setFilter(o.value)}
                className={`text-xs px-3 py-1.5 border transition-all ${
                  filter === o.value
                    ? "border-gold text-gold"
                    : "border-white/15 text-white/50 hover:border-white/40"
                }`}
              >
                {o.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Bookings list */}
        {loading && bookings.length === 0 ? (
          <div className="text-center text-white/40 py-20">Loading…</div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center text-white/40 py-20 border border-white/10 border-dashed"
            data-testid="empty-state"
          >
            No enquiries here yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* List */}
            <div className="lg:col-span-2 space-y-3" data-testid="booking-list">
              {filtered.map((b) => {
                const meta = statusMeta(b.status);
                const isActive = selected?.id === b.id;
                return (
                  <button
                    key={b.id}
                    data-testid={`booking-item-${b.id}`}
                    onClick={() => setSelected(b)}
                    className={`w-full text-left p-5 border transition-all duration-300 ${
                      isActive
                        ? "border-gold/60 bg-[#1a1a1d]"
                        : "border-white/10 bg-[#18181b]/50 hover:border-white/30"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-display text-xl text-ivory truncate">
                            {b.name}
                          </h3>
                          <span
                            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${meta.color}`}
                          >
                            {meta.label}
                          </span>
                        </div>
                        <p className="text-sm text-white/50 mt-1 truncate">
                          {b.event_type} · {b.event_date} · {b.location}
                        </p>
                      </div>
                      <span className="text-xs text-white/40 flex-shrink-0">
                        {formatDate(b.created_at)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            <div className="lg:col-span-1">
              {selected ? (
                <BookingDetail
                  key={selected.id}
                  booking={selected}
                  onStatusChange={(status) => updateStatus(selected.id, status)}
                  onSaveNotes={(notes) => saveNotes(selected.id, notes)}
                  onDelete={() => removeBooking(selected.id)}
                />
              ) : (
                <div className="border border-white/10 border-dashed p-10 text-center text-white/40 text-sm sticky top-24">
                  Select an enquiry to see details.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function BookingDetail({ booking, onStatusChange, onSaveNotes, onDelete }) {
  const [notesDraft, setNotesDraft] = useState(booking.notes || "");
  useEffect(() => {
    setNotesDraft(booking.notes || "");
  }, [booking.id, booking.notes]);

  const meta = statusMeta(booking.status);

  return (
    <div
      className="border border-white/10 bg-[#18181b]/70 backdrop-blur-md p-6 sticky top-24"
      data-testid="booking-detail"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-2xl text-ivory" data-testid="detail-name">
            {booking.name}
          </h2>
          <p className="text-xs text-white/40 mt-1">
            Submitted {formatDate(booking.created_at)}
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${meta.color}`}
        >
          {meta.label}
        </span>
      </div>

      <div className="space-y-3 text-sm border-t border-white/10 pt-5">
        <a
          href={`mailto:${booking.email}`}
          className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors group"
          data-testid="detail-email"
        >
          <Mail className="w-3.5 h-3.5 text-gold flex-shrink-0" />
          <span className="truncate">{booking.email}</span>
          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50 ml-auto" />
        </a>
        <a
          href={`tel:${booking.phone}`}
          className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors"
          data-testid="detail-phone"
        >
          <Phone className="w-3.5 h-3.5 text-gold flex-shrink-0" />
          <span>{booking.phone}</span>
        </a>
        <div className="flex items-center gap-3 text-white/80" data-testid="detail-date">
          <Calendar className="w-3.5 h-3.5 text-gold flex-shrink-0" />
          <span>{booking.event_date} · {booking.event_type}</span>
        </div>
        <div className="flex items-center gap-3 text-white/80" data-testid="detail-location">
          <MapPin className="w-3.5 h-3.5 text-gold flex-shrink-0" />
          <span>{booking.location}</span>
        </div>
        {booking.guest_count && (
          <div className="flex items-center gap-3 text-white/80" data-testid="detail-guests">
            <Users className="w-3.5 h-3.5 text-gold flex-shrink-0" />
            <span>{booking.guest_count} guests</span>
          </div>
        )}
      </div>

      {booking.message && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
            <MessageSquare className="w-3 h-3" />
            Their message
          </div>
          <p
            className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap"
            data-testid="detail-message"
          >
            {booking.message}
          </p>
        </div>
      )}

      {/* Status changer */}
      <div className="mt-6 pt-5 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
          Update status
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              data-testid={`status-btn-${o.value}`}
              onClick={() => onStatusChange(o.value)}
              disabled={booking.status === o.value}
              className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-all ${
                booking.status === o.value
                  ? `${o.color} cursor-default`
                  : "border-white/15 text-white/60 hover:border-gold/60 hover:text-gold"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="mt-6 pt-5 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
          Internal notes
        </div>
        <textarea
          data-testid="detail-notes"
          rows={4}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Quote sent, follow up Friday, etc..."
          className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ivory placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors resize-none"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <button
            data-testid="detail-save-notes"
            onClick={() => onSaveNotes(notesDraft)}
            disabled={notesDraft === (booking.notes || "")}
            className="text-xs px-4 py-2 bg-ivory text-obsidian hover:bg-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Save notes
          </button>
          <button
            data-testid="detail-delete"
            onClick={onDelete}
            className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
